import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import useDrawsUpdated from "./useDrawsUpdated";

const API = process.env.REACT_APP_BACKEND_URL;

// Module-level cache so every component gets the same data without refetching
let _cache = { data: null, ts: 0 };
let _inflight = null;

async function _fetch() {
  if (_inflight) return _inflight;
  _inflight = axios.get(`${API}/api/consensus/tier-vector`)
    .then(res => {
      _cache = { data: res.data, ts: Date.now() };
      return res.data;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

/**
 * useConsensusGroups — single source of truth for GREEN/YELLOW/RED tier per number 1-50.
 * Auto-refreshes when a new draw is added.
 *
 * Returns: { groups, groupOf(n), scoreOf(n), loading, error }
 *   - groups: { GREEN: [...], YELLOW: [...], RED: [...] }
 *   - groupOf(n): "GREEN" | "YELLOW" | "RED"
 *   - scoreOf(n): consensus score (integer)
 */
export default function useConsensusGroups() {
  const [state, setState] = useState({ data: _cache.data, loading: !_cache.data, error: null });
  const mountedRef = useRef(true);

  const load = useCallback(async () => {
    try {
      const d = await _fetch();
      if (mountedRef.current) setState({ data: d, loading: false, error: null });
    } catch (e) {
      if (mountedRef.current) setState(s => ({ ...s, loading: false, error: e?.message || "Failed" }));
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (!_cache.data) load();
    return () => { mountedRef.current = false; };
  }, [load]);

  // Auto-refresh when a new draw is added (bust cache then reload)
  useDrawsUpdated(() => {
    _cache = { data: null, ts: 0 };
    load();
  });

  const data = state.data;
  const numberGroup = data?.groups?.number_group || {};
  const rows = data?.rows || [];
  const scoreMap = {};
  for (const r of rows) scoreMap[r.number] = r.consensus_score;

  const groupOf = (n) => numberGroup[n] || "RED";
  const scoreOf = (n) => scoreMap[n] ?? 0;

  return {
    groups: {
      GREEN: Object.entries(numberGroup).filter(([, v]) => v === "GREEN").map(([k]) => parseInt(k)),
      YELLOW: Object.entries(numberGroup).filter(([, v]) => v === "YELLOW").map(([k]) => parseInt(k)),
      RED: Object.entries(numberGroup).filter(([, v]) => v === "RED").map(([k]) => parseInt(k)),
    },
    numberGroup,
    groupOf,
    scoreOf,
    loading: state.loading,
    error: state.error,
  };
}
